import styles from './Contato.module.css';
import ParticlesBackground from '../../components/ParticlesBackground/ParticlesBackground';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaWhatsapp, FaEnvelope, FaCircleCheck } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import PageSEO from '../../components/PageSEO/PageSEO';

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

function Contato() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || 'pt';
  const [isProcessing, setProcessing] = useState(false);
  const [sent, setSent] = useState(false);

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    mensagem: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'telefone') {
      setFormData((prev) => ({ ...prev, telefone: formatTelefone(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const formatTelefone = (raw) => {
    // Permite padrão internacional: mantém o + inicial e formata livremente
    if (raw.startsWith('+')) {
      const digits = raw.slice(1).replace(/\D/g, '');
      return '+' + digits;
    }
    // Padrão Brasil: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
    const digits = raw.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2)  return digits.replace(/(\d{1,2})/, '($1');
    if (digits.length <= 6)  return digits.replace(/(\d{2})(\d+)/, '($1) $2');
    if (digits.length <= 10) return digits.replace(/(\d{2})(\d{4})(\d+)/, '($1) $2-$3');
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const enviarEmail = async (dados) => {
    setProcessing(true);
    try {
      const credenciais = btoa('steinsoft2025:ss@2025!#');
      await fetch(`${import.meta.env.VITE_API_URL}/EnviarEmail`, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${credenciais}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dados),
      });
      setFormData({ nome: '', email: '', telefone: '', mensagem: '' });
      setSent(true);
      setTimeout(() => setSent(false), 6000);
    } catch (error) {
      console.error('Erro de comunicação com o servidor:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    enviarEmail(formData);
  };

  return (
    <>
      <PageSEO
        title="Contato"
        description="Entre em contato com a SteinSoft. Fale conosco pelo WhatsApp, e-mail ou pelo formulário para tirar dúvidas, solicitar orçamentos e iniciar projetos."
        path="/Contato"
        lang={lang}
      />
      <div className={styles.particles}>
        <ParticlesBackground />
      </div>

      <div className={styles.page}>

        {/* ── Hero ── */}
        <section className={styles.hero}>
          <motion.span className={styles.eyebrow} {...reveal()}>
            {t('contato.eyebrow')}
          </motion.span>
          <motion.h1 className={styles.heroTitle} {...reveal(0.1)}>
            {t('contato.titulo')} <span className={styles.accent}>{t('contato.tituloDest')}</span>
          </motion.h1>
          <motion.p className={styles.heroSub} {...reveal(0.2)}>
            {t('contato.subtitulo')}
          </motion.p>
        </section>

        {/* ── Layout ── */}
        <section className={styles.section}>
          <div className={styles.grid}>

            {/* Info */}
            <motion.div className={styles.info} {...reveal(0.1)}>
              <h2 className={styles.infoTitle}>
                {t('contato.infoTitulo')}
              </h2>
              <p className={styles.infoDesc}>
                {t('contato.infoDesc')}
              </p>

              <div className={styles.contacts}>
                <a
                  className={styles.contactItem}
                  href="https://wa.me/5548883934 47"
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className={styles.contactIcon}>
                    <FaWhatsapp />
                  </div>
                  <div className={styles.contactText}>
                    <span className={styles.contactLabel}>WhatsApp</span>
                    <span className={styles.contactValue}>+55 48 8839-3447</span>
                  </div>
                </a>

                <a
                  className={styles.contactItem}
                  href="mailto:contato@steinsoft.com.br"
                >
                  <div className={styles.contactIcon}>
                    <FaEnvelope />
                  </div>
                  <div className={styles.contactText}>
                    <span className={styles.contactLabel}>E-mail</span>
                    <span className={styles.contactValue}>contato@steinsoft.com.br</span>
                  </div>
                </a>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div className={styles.formWrap} {...reveal(0.2)}>
              <div className={styles.glow1} />
              <div className={styles.glow2} />

              <AnimatePresence mode="wait">
                {sent ? (
                  <motion.div
                    key="success"
                    className={styles.successBox}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <FaCircleCheck className={styles.successIcon} />
                    <h3 className={styles.successTitle}>{t('contato.sucessoTitulo')}</h3>
                    <p className={styles.successDesc}>
                      {t('contato.sucessoDesc')}
                    </p>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit}
                    className={styles.form}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className={styles.row}>
                      <div className={styles.field}>
                        <input
                          type="text"
                          name="nome"
                          value={formData.nome}
                          onChange={handleChange}
                          placeholder={t('contato.placeholderNome')}
                          required
                          className={styles.input}
                        />
                      </div>
                      <div className={styles.field}>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder={t('contato.placeholderEmail')}
                          required
                          className={styles.input}
                        />
                      </div>
                    </div>

                    <div className={styles.field}>
                      <input
                        type="tel"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleChange}
                        placeholder={t('contato.placeholderTelefone')}
                        className={styles.input}
                      />
                    </div>

                    <div className={styles.field}>
                      <textarea
                        name="mensagem"
                        value={formData.mensagem}
                        onChange={handleChange}
                        placeholder={t('contato.placeholderMensagem')}
                        required
                        className={styles.textarea}
                      />
                    </div>

                    <button
                      type="submit"
                      className={styles.btnSubmit}
                      disabled={isProcessing}
                    >
                      {isProcessing ? t('contato.enviando') : t('contato.enviar')}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>

          </div>
        </section>

      </div>
    </>
  );
}

export default Contato;
